/// <reference lib="webworker" />

import { initializeApp } from 'firebase/app';
import { getMessaging, onBackgroundMessage } from 'firebase/messaging/sw';
import {
  handleFcmPayload,
  type PrepareForPolicyUpdate,
} from './handleFcmPayload';
import { convertBase64UrlToUint8Array } from '../lib/encoding';
import { captureException } from '../helper/sentry';
import * as qinit from '../qinit';
import { PUSH_TAG } from './constants';

// This file runs in Service Worker context where `self` is ServiceWorkerGlobalScope
declare const self: ServiceWorkerGlobalScope;

/**
 * Ensure push subscription with userVisibleOnly: false
 *
 * Prevents Chrome from showing "site has been updated in the background" notifications.
 * Must be called before Firebase's getToken() to ensure our subscription config is used.
 *
 * @throws Error when notifications not available, permissions not granted, or subscription fails
 */
export const ensurePushSubscription = async (): Promise<void> => {
  // Check notification API availability
  if (!('Notification' in self)) {
    console.warn(`${PUSH_TAG} Notification API not available`);
    return;
  }

  // Check notification permission
  if (Notification.permission !== 'granted') {
    console.warn(
      `${PUSH_TAG} Notification permission ${Notification.permission}`
    );
    return;
  }

  const registration = self.registration;
  const existing = await registration.pushManager.getSubscription();

  if (existing) {
    return;
  }

  const vapidKey =
    qinit.tenant.chromebook.pushNotificationConfig?.vapidKey?.key;
  if (!vapidKey) {
    console.warn(`${PUSH_TAG} VapidKey config not available`);
    return;
  }

  const applicationServerKey = convertBase64UrlToUint8Array(vapidKey);

  await registration.pushManager.subscribe({
    userVisibleOnly: false,
    applicationServerKey,
  });
};

/**
 * Firebase Messaging setup (Service Worker only)
 *
 * Initializes Firebase and synchronously registers the `onBackgroundMessage`
 * listener for push notifications.
 *
 * **IMPORTANT**: This function must be called synchronously during the initial
 * Service Worker script evaluation, before any async code. Chrome requires
 * push event listeners to be registered synchronously, otherwise FCM messages
 * will not be received.
 *
 * Only reports unexpected errors to Sentry (bugs, config issues).
 * Expected errors (IndexedDB unavailable, permissions issues) are logged but not reported.
 */
export const setupFirebaseMessaging = (
  prepareForPolicyUpdate: PrepareForPolicyUpdate
): void => {
  try {
    const firebaseConfig =
      qinit.tenant.chromebook.pushNotificationConfig?.firebase;
    if (!firebaseConfig) {
      console.warn(`${PUSH_TAG} Config not available`);
      return;
    }

    const firebase = initializeApp(firebaseConfig);
    const messaging = getMessaging(firebase);
    onBackgroundMessage(messaging, payload =>
      handleFcmPayload(payload, prepareForPolicyUpdate)
    );
  } catch (error) {
    const err = error as Error;
    console.error(`${PUSH_TAG} Setup failed:`, err.message);
    captureException(err);
  }
};
